/**
 * Earthworks quotes (EW-2, fieldiq-api feat/ew-2-quotes) on the shared quote
 * screens: the Earthworks Rate Card as a pricing source, the customer's
 * Sent → Viewed → Accepted / Declined status, "Create Earthworks Job" on an
 * accepted quote, and every refusal those doors answer with as a sentence.
 * Well Testing's lib/wt-quotes.ts, rule for rule, on the Earthworks card.
 *
 * Every helper answers for an Earthworks item, line or quote only and returns
 * null for anything else, so the shared screens keep exactly what they
 * rendered before for every other vertical.
 *
 * THE API DECIDES. Nothing here prices a line or decides a status: "Viewed" is
 * the API's (`customer_viewed_at` on the list, `customerStatus` on the state).
 */
import { ewApiError } from './earthworks';
import type { EwQuoteCustomerStatus } from '../types/earthworks';

export const EW_QUOTE_VERTICAL = 'earthworks';
export const EW_QUOTE_SOURCE_KIND = 'ew_rate';

/** The terms beside a rate-card item, never priced: code · kind · how it prices · notes. */
export function ewQuoteItemTerms(item: { sourceKind?: string | null; terms?: Record<string, unknown> | null }): string | null {
  if (item.sourceKind !== EW_QUOTE_SOURCE_KIND) return null;
  const t = item.terms ?? {};
  const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null);
  const pricing = str(t.pricing);
  const PRICING: Record<string, string> = { lump_sum: 'Lump sum', unit_price: 'Unit price', t_and_m: 'T&M' };
  const parts = [str(t.code), str(t.kindLabel), pricing ? PRICING[pricing] ?? null : null, str(t.appliesTo), str(t.notes)]
    .filter((x): x is string => !!x);
  return parts.length ? parts.join(' · ') : null;
}

/** The currency an Earthworks quote's money is spelled in: the quote row's own code. Null elsewhere. */
export function ewQuoteCurrency(quote: { vertical?: string | null; currency?: string | null } | null | undefined): string | null {
  if (!quote || quote.vertical !== EW_QUOTE_VERTICAL) return null;
  return quote.currency === 'CAD' || quote.currency === 'USD' ? quote.currency : null;
}

/**
 * The unit an Earthworks line is priced in, in words. The API writes every
 * such line as line_item_type 'other' and stamps the unit in the shared per_*
 * vocabulary (api ewQuoteUnit). Null for any other line; an unknown unit is `—`.
 */
const EW_QUOTE_UNIT_WORDS: Record<string, string> = {
  per_hour: 'Per hour',
  per_day: 'Per day',
  each: 'Each',
  per_load: 'Per load',
  per_m3: 'Per m³',
  per_yd3: 'Per yd³',
  per_tonne: 'Per tonne',
  per_ton: 'Per ton (US)',
  per_m2: 'Per m²',
  per_ft2: 'Per ft²',
  per_metre: 'Per m',
  per_foot: 'Per ft',
  per_km: 'Per km',
  per_mile: 'Per mile',
  per_pile: 'Per pile',
  flat_fee: 'Flat',
};
export function ewQuoteLineKind(line: { source_kind?: string | null; unit?: string | null }): string | null {
  if (line.source_kind !== EW_QUOTE_SOURCE_KIND) return null;
  return (line.unit && EW_QUOTE_UNIT_WORDS[line.unit]) || '—';
}

/** The rate-card unit (ew_rates.unit) in words, for a job line. Absent → `—`. */
const EW_RATE_UNIT_WORDS: Record<string, string> = {
  hour: 'Per hour', day: 'Per day', each: 'Each', load: 'Per load', m3: 'Per m³', yd3: 'Per yd³',
  tonne: 'Per tonne', ton: 'Per ton (US)', m2: 'Per m²', ft2: 'Per ft²', m: 'Per m', ft: 'Per ft',
  km: 'Per km', mi: 'Per mile', pile: 'Per pile', flat: 'Flat',
};
export const ewRateUnitWords = (unit: string | null | undefined): string => (unit && EW_RATE_UNIT_WORDS[unit]) || '—';

/**
 * The status a quote LIST row shows. An Earthworks quote that is sent and whose
 * link the customer has opened reads "Viewed"; everything else is the row's own
 * status, unchanged.
 */
export function quoteListStatus(q: { vertical?: string | null; status: string; customer_viewed_at?: string | null }): string {
  if (q.vertical === EW_QUOTE_VERTICAL && q.status === 'sent' && q.customer_viewed_at) return 'viewed';
  return q.status;
}

export const EW_CUSTOMER_STATUS_LABEL: Record<EwQuoteCustomerStatus, string> = {
  draft: 'Draft',
  sent: 'Sent — not opened yet',
  viewed: 'Viewed by the customer',
  accepted: 'Accepted',
  declined: 'Declined',
  expired: 'Expired',
};

/** receivedMethod written by the Stripe webhook (Batch 4b) — never offered in the office's method list. */
export const EW_DEPOSIT_STRIPE_METHOD = 'stripe';

export const EW_DEPOSIT_METHODS = [
  { value: 'cheque', label: 'Cheque' },
  { value: 'eft', label: 'EFT' },
  { value: 'wire', label: 'Wire' },
  { value: 'credit_card', label: 'Credit card' },
  { value: 'cash', label: 'Cash' },
  { value: 'other', label: 'Other' },
] as const;

/**
 * Every refusal the Earthworks quote doors answer with. `api: true` means the
 * API's own words win when it sent some.
 */
const EW_QUOTE_REFUSALS: Record<string, { api: boolean; sentence: string }> = {
  // POST /quotes/from-pricing-source
  QUOTE_ITEM_NOT_AVAILABLE: { api: true, sentence: 'One of the picked rates no longer applies to this customer today. Go back and pick again.' },
  QUOTE_CURRENCY_MISMATCH: { api: true, sentence: "One of the picked rates is in a different currency from the company's, so it cannot be quoted." },
  QUOTE_QUANTITY_INVALID: { api: true, sentence: 'Enter a quantity greater than zero on every picked line.' },
  QUOTE_LINE_UNPRICED: { api: true, sentence: 'One of the picked lines has no rate on the Earthworks Rate Card, so it cannot be quoted.' },
  VERTICAL_NOT_ENABLED: { api: false, sentence: 'Earthworks is not turned on for this company, so it cannot be quoted.' },
  // POST /quotes/:id/create-ew-job
  QUOTE_NOT_FOUND: { api: false, sentence: 'This quote was not found. It may have been deleted.' },
  QUOTE_NOT_EARTHWORKS: { api: true, sentence: 'Only an Earthworks quote becomes an Earthworks job.' },
  QUOTE_NOT_ACCEPTED: { api: true, sentence: 'Accept the quote before creating its Earthworks job.' },
  QUOTE_CUSTOMER_REQUIRED: { api: true, sentence: 'The quote has no customer to create an Earthworks job for.' },
  SITE_REQUIRED: { api: false, sentence: "Pick the site this job is on. An Earthworks job always belongs to one of the customer's sites." },
  site_customer_mismatch: { api: false, sentence: "That site belongs to a different customer. Pick one of this quote's customer's sites." },
  quote_job_already_created: { api: true, sentence: "This quote's Earthworks job was just created. Refresh to see it." },
  job_number_contended: { api: false, sentence: 'Another job took that number at the same moment. Try again.' },
  invalid_request: { api: true, sentence: 'Something on the form is not valid. Check it and try again.' },
  // /earthworks/quotes/:id/deposit
  quote_closed: { api: true, sentence: 'A declined or expired quote cannot take a deposit.' },
  deposit_received: { api: true, sentence: 'The deposit has been received. Undo that before changing it.' },
  deposit_not_set: { api: true, sentence: 'Set the deposit before marking it received.' },
  deposit_applied: { api: true, sentence: 'The deposit has been carried to an invoice.' },
  quote_total_zero: { api: true, sentence: 'The quote has no total to take a percentage of.' },
  deposit_zero: { api: true, sentence: 'That percentage of the quote total rounds to $0.00.' },
  not_found: { api: false, sentence: 'That record was not found. It may have been deleted.' },
};

export const EW_QUOTE_REFUSAL_CODES = Object.keys(EW_QUOTE_REFUSALS);

/** A refused Earthworks quote write as `{ code, error }` — the shape the quote screens' refusal notice draws. */
export function ewQuoteRefusal(err: unknown, fallback: string): { code?: string; error: string } {
  const e = ewApiError(err);
  const code = e.code ?? undefined;
  if (e.status === 403 && code !== 'VERTICAL_NOT_ENABLED') {
    return { code, error: 'You do not have permission to do this. Ask an admin or office staff.' };
  }
  const known = code ? EW_QUOTE_REFUSALS[code] : undefined;
  if (known) return { code, error: known.api && e.message ? e.message : known.sentence };
  if (e.status === 404) return { code, error: EW_QUOTE_REFUSALS.not_found.sentence };
  return { code, error: e.message ?? fallback };
}
